import { Segment, Menu, Header, Grid, InputOnChangeData, Input, Button } from "semantic-ui-react";
import { Project } from "../models/Project";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { useEffect, useState } from "react";
import { toastError, toastSuccess } from "../utils";
import EditProjectModal from "./EditProjectModal";
import EditPermitRegisterModal from "./EditPermitRegisterModal";
import ProjectViewContainer from "../containers/ProjectViewContainer";
import { EmergencyContact } from "../models/EmergencyContact";
import { GeoJSONPolygonType } from "../models/GeoJSONPolygonType";
import PermitRegistersGrid from "./PermitRegistersGrid";
import { PermitRegister } from "../models/PermitRegister";
import { PermitType } from "../models/PermitType";
import SetMaxProjectAssignmentsModal from "./SetMaxProjectAssignmentsModal";
import { useOrganizationPolicies } from "../hooks/useOrganizationPolicies";

interface Props {
  showProjectBoundariesOnMap?: boolean;
  isProjectsLoading: boolean;
  projects?: Project[] | null;
  onSearchProjects: (searchStrign: string) => void;
  onAddProject: (
    name: string,
    number: string,
    location: string,
    emergencyContact: EmergencyContact,
    boundaries?: GeoJSONPolygonType,
  ) => void;
  addProjectStatus: AsyncActionStatus;
  onEditProject: (
    id: string,
    name: string,
    number: string,
    location: string,
    emergencyContact: EmergencyContact,
    boundaries?: GeoJSONPolygonType,
  ) => void;
  editProjectStatus: AsyncActionStatus;
  onDeleteProject: (id: string) => void;
  deleteProjectStatus: AsyncActionStatus;
  onAddPermitRegister: (title: string, description: string, projectId: string, permitTypeIds: string[]) => void;
  addPermitRegisterStatus: AsyncActionStatus;
  onUpdatePermitRegister: (id: string, title: string, description: string, permitTypeIds: string[]) => void;
  updatePermitRegisterStatus: AsyncActionStatus;
  onDeletePermitRegister: (id: string) => void;
  deletePermitRegisterStatus: AsyncActionStatus;
  onSetMaxProjectAssignments: (projectId: string, max: number | null) => void;
  setMaxStatus: AsyncActionStatus;
  permitTypes?: PermitType[];
}

const ProjectsList = ({
  showProjectBoundariesOnMap,
  isProjectsLoading,
  projects,
  onSearchProjects,
  onAddProject,
  addProjectStatus,
  onEditProject,
  editProjectStatus,
  onDeleteProject,
  deleteProjectStatus,
  onAddPermitRegister,
  addPermitRegisterStatus,
  onUpdatePermitRegister,
  updatePermitRegisterStatus,
  onDeletePermitRegister,
  deletePermitRegisterStatus,
  onSetMaxProjectAssignments,
  setMaxStatus,
  permitTypes,
}: Props) => {
  const [openEditProjectModal, setOpenEditProjectModal] = useState(false);
  const [openEditRegisterModal, setOpenEditRegisterModal] = useState(false);
  const [openViewProjectSection, setOpenViewProjectSection] = useState(false);
  const [openSetMaxModal, setOpenSetMaxModal] = useState(false);
  const [selectedProjectToView, setSelectedProjectToView] = useState<Project | undefined>(undefined);
  const [selectedProjectToEdit, setSelectedProjectToEdit] = useState<Project | undefined>(undefined);
  const [selectedProjectForMax, setSelectedProjectForMax] = useState<Project | undefined>(undefined);
  const [selectedRegisterToView, setSelectedRegisterToView] = useState<PermitRegister | undefined>(undefined);
  const [selectedRegisterToEdit, setSelectedRegisterToEdit] = useState<PermitRegister | undefined>(undefined);
  const { authorizedToManageProjectAssignmentCap } = useOrganizationPolicies();

  const handleEditProject = (projectId: string) => {
    const project = projects?.find((o) => o.id === projectId);
    if (project) {
      setSelectedProjectToEdit({ ...project } as Project);
      setOpenEditProjectModal(true);
    }
  };

  const handleEditRegister = (registerId: string) => {
    const permitRegister = projects?.flatMap((p) => p.permitRegisters ?? []).find((r) => r.id === registerId);
    const project = projects?.find((p) => p.permitRegisters?.some((r) => r.id === registerId));
    if (permitRegister && project) {
      setSelectedRegisterToEdit({ ...permitRegister } as PermitRegister);
      setSelectedProjectToEdit({ ...project } as Project);
      setOpenEditRegisterModal(true);
    }
  };

  const handleNewRegister = (project: Project) => {
    setSelectedProjectToEdit({ ...project } as Project);
    setSelectedRegisterToEdit(undefined);
    setOpenEditRegisterModal(true);
  };

  const handleDeleteRegister = (registerId: string) => {
    onDeletePermitRegister(registerId);
  };

  const handleDeleteProject = (projectId: string) => {
    onDeleteProject(projectId);
  };

  useEffect(() => {
    if (deleteProjectStatus.successful) {
      toastSuccess("Project deleted successfully!");
      resetMutationStatuses();
    } else if (deleteProjectStatus.errors) {
      toastError(deleteProjectStatus.errors.toString());
      resetMutationStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteProjectStatus]);

  const handleModalClose = () => {
    setOpenEditProjectModal(false);
    setOpenEditRegisterModal(false);
    setOpenSetMaxModal(false);
    setSelectedProjectToEdit(undefined);
    setSelectedRegisterToEdit(undefined);
    setSelectedProjectForMax(undefined);

    resetMutationStatuses();
  };

  const resetMutationStatuses = () => {
    addProjectStatus.reset();
    editProjectStatus.reset();
    deleteProjectStatus.reset();
    addPermitRegisterStatus.reset();
    updatePermitRegisterStatus.reset();
    deletePermitRegisterStatus.reset();
    setMaxStatus.reset();
  };

  const handleEditProjectModalSubmit = (
    name: string,
    number: string,
    location: string,
    emergencyContact: EmergencyContact,
    boundaries?: GeoJSONPolygonType,
    id?: string,
  ) => {
    if (id) {
      onEditProject(id, name, number, location, emergencyContact, boundaries);
    } else {
      onAddProject(name, number, location, emergencyContact, boundaries);
    }
  };

  useEffect(() => {
    if (addProjectStatus.successful) {
      toastSuccess("Project added successfully!");
      setOpenEditProjectModal(false);
      setSelectedProjectToEdit(undefined);
    }
  }, [addProjectStatus.successful]);

  useEffect(() => {
    if (editProjectStatus.successful) {
      toastSuccess("Project updated successfully!");
      setOpenEditProjectModal(false);
      setSelectedProjectToEdit(undefined);
    }
  }, [editProjectStatus.successful]);

  const handleRegisterModalSubmit = (
    title: string,
    description: string,
    permitTypeIds: string[],
    id?: string,
    projectId?: string,
  ) => {
    if (id) {
      onUpdatePermitRegister(id, title, description, permitTypeIds);
    } else if (projectId) {
      onAddPermitRegister(title, description, projectId, permitTypeIds);
    }
  };

  useEffect(() => {
    if (addPermitRegisterStatus.successful) {
      toastSuccess("Register created successfully!");
      setOpenEditRegisterModal(false);
      setSelectedProjectToEdit(undefined);
    }
  }, [addPermitRegisterStatus.successful]);

  useEffect(() => {
    if (updatePermitRegisterStatus.successful) {
      toastSuccess("Register updated successfully!");
      setOpenEditRegisterModal(false);
      setSelectedRegisterToEdit(undefined);
      setSelectedProjectToEdit(undefined);
    }
  }, [updatePermitRegisterStatus.successful]);

  useEffect(() => {
    if (deletePermitRegisterStatus.successful) {
      toastSuccess("Register deleted successfully!");
      resetMutationStatuses();
    } else if (deletePermitRegisterStatus.errors) {
      toastError(deletePermitRegisterStatus.errors.toString());
      resetMutationStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletePermitRegisterStatus]);

  const handleSetMax = (project: Project) => {
    setSelectedProjectForMax(project);
    setOpenSetMaxModal(true);
  };

  const handleSetMaxSubmit = (max: number | null) => {
    if (selectedProjectForMax) {
      onSetMaxProjectAssignments(selectedProjectForMax.id, max);
    }
  };

  useEffect(() => {
    if (setMaxStatus.successful) {
      toastSuccess("Maximum assignments updated successfully!");
      setOpenSetMaxModal(false);
      setSelectedProjectForMax(undefined);
    }
  }, [setMaxStatus.successful]);

  const handleRegisterClicked = (project: Project, register: PermitRegister) => {
    setOpenViewProjectSection(true);
    setSelectedRegisterToView(register);
    setSelectedProjectToView(project);
  };

  const handleViewProjectSectionClose = () => {
    setSelectedRegisterToView(undefined);
    setSelectedProjectToView(undefined);
    setOpenViewProjectSection(false);
  };

  const handleSearchStringChange = (e: any, { name, value }: InputOnChangeData) => {
    onSearchProjects(value);
  };

  const projectCard = (project: Project) => {
    return (
      <>
        <Segment attached="top" secondary basic compact key={project.id} style={{ padding: "0.5em 1em" }}>
          <Button
            circular
            icon="trash"
            title="Delete project"
            floated="right"
            style={{ padding: "5px 8px", marginLeft: "8px", "box-shadow": "0px 0px" }}
            onClick={() => handleDeleteProject(project.id)}
          ></Button>
          <Button
            circular
            icon="edit"
            title="Edit project"
            floated="right"
            style={{ padding: "5px 8px", marginLeft: "8px", "box-shadow": "0px 0px" }}
            onClick={() => handleEditProject(project.id)}
          ></Button>
          {authorizedToManageProjectAssignmentCap && (
            <Button
              circular
              title="Set max assignments"
              floated="right"
              style={{ padding: "5px 8px", marginLeft: "8px", "box-shadow": "0px 0px" }}
              onClick={() => handleSetMax(project)}
            >
              Limit team size
            </Button>
          )}
          <Button
            circular
            floated="right"
            style={{ padding: "5px 8px", marginLeft: "8px", "box-shadow": "0px 0px" }}
            onClick={() => handleNewRegister(project)}
          >
            Add new register
          </Button>
          <Header as="h4" style={{ display: "inline" }}>
            {project.name}
          </Header>
        </Segment>

        <Segment attached style={{ borderTop: "3px !important" }}>
          {project.permitRegisters && (
            <Grid columns="1">
              <Grid.Row>
                <Grid.Column>
                  <PermitRegistersGrid
                    permitRegisters={project.permitRegisters}
                    selectedPermitRegister={selectedRegisterToView}
                    onPermitRegisterClicked={(register) => handleRegisterClicked(project, register)}
                    onEditPermitRegister={handleEditRegister}
                    onDeletePermitRegister={handleDeleteRegister}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </Segment>
      </>
    );
  };

  return (
    <>
      <Segment basic>
        <Header as="h2">Projects</Header>
      </Segment>

      <Menu pointing secondary color="green">
        <Menu.Item icon="plus" name="New project" onClick={(e) => setOpenEditProjectModal(true)} />

        <Menu.Menu position="right">
          <Menu.Item>
            <Input icon="search" placeholder="Search..." onChange={handleSearchStringChange} />
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      <Segment basic loading={isProjectsLoading}>
        <Grid columns={openViewProjectSection ? "2" : "1"}>
          <Grid.Row>
            <Grid.Column width={openViewProjectSection ? "10" : "16"}>{projects?.map(projectCard)}</Grid.Column>
            {openViewProjectSection && (
              <Grid.Column width="6">
                <ProjectViewContainer
                  showProjectBoundariesOnMap={showProjectBoundariesOnMap}
                  onClose={handleViewProjectSectionClose}
                  project={projects?.find((p) => p.id === selectedProjectToView?.id)}
                  permitRegister={projects
                    ?.find((p) => p.id === selectedProjectToView?.id)
                    ?.permitRegisters?.find((r) => r.id === selectedRegisterToView?.id)}
                  permitTypes={permitTypes}
                ></ProjectViewContainer>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Segment>

      <EditProjectModal
        showProjectBoundariesOnMap={showProjectBoundariesOnMap}
        open={openEditProjectModal}
        onClose={handleModalClose}
        onSubmit={handleEditProjectModalSubmit}
        submitInProgress={addProjectStatus.loading || editProjectStatus.loading}
        project={selectedProjectToEdit}
        submitErrors={
          addProjectStatus.errors
            ? addProjectStatus.errors
            : editProjectStatus.errors
              ? editProjectStatus.errors
              : undefined
        }
      ></EditProjectModal>

      <EditPermitRegisterModal
        open={openEditRegisterModal}
        onClose={handleModalClose}
        onSubmit={handleRegisterModalSubmit}
        submitInProgress={addPermitRegisterStatus.loading || updatePermitRegisterStatus.loading}
        permitRegister={selectedRegisterToEdit}
        projectId={selectedProjectToEdit?.id}
        availablePermitTypes={permitTypes?.filter(
          (t) => !selectedRegisterToEdit?.permitTypes?.some((rt) => rt.id === t.id),
        )}
        submitErrors={
          addPermitRegisterStatus.errors
            ? addPermitRegisterStatus.errors
            : updatePermitRegisterStatus.errors
              ? updatePermitRegisterStatus.errors
              : undefined
        }
      />

      <SetMaxProjectAssignmentsModal
        open={openSetMaxModal}
        onClose={handleModalClose}
        onSubmit={handleSetMaxSubmit}
        submitInProgress={setMaxStatus.loading}
        currentMax={selectedProjectForMax?.maxProjectAssignments}
        errors={setMaxStatus.errors}
      />
    </>
  );
};

export default ProjectsList;
